import { resolveCustomerCameraScopeLabel } from '../../utils/customerPlaceLabel';
import type { Incident } from '../../services/incidentsApi';

/**
 * EPIC284 Overview slice 2 — evidence still contract.
 *
 * Reuses Investigate's media path: list incidents for the condition scope,
 * require a real AVAILABLE/READY snapshot (or snapshot_uri), then load
 * `/evidence/latest/snapshot`. Never invent placeholders.
 *
 * Zone intrusion conditions store evidence as zone_violation (danger-zone
 * patterns) — thumbs must probe that stored type, not the family name.
 *
 * List probes must stay undated for thumbs: multi-day from_date/to_date
 * listIncidents on yyz has hung (>20s, 0 bytes) and blanked stills via the
 * 12s Overview budget before any snapshot fetch ran. Window dates remain on
 * Investigate deep-links only.
 */

export type OverviewEvidenceThumbRequest = {
  conditionId: string;
  zoneId?: string | null;
  cameraId?: string | null;
  /** Primary stored incident_type for this condition (e.g. zone_violation). */
  incidentType?: string | null;
  /** Extra stored types to try when the primary list is empty. */
  candidateIncidentTypes?: string[];
  windowStart?: string | null;
  windowEnd?: string | null;
};

export type OverviewEvidenceThumbStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'empty'
  | 'error';

export type OverviewEvidenceThumbState = {
  status: OverviewEvidenceThumbStatus;
  imageUrl: string | null;
  emptyLabel: string;
  incidentId: string | null;
};

/** Cap incident list probe per condition — Overview must stay light. */
export const OVERVIEW_EVIDENCE_THUMB_INCIDENT_LIMIT = 8;

/** Hard ceiling so thumbs never spin forever on slow/hung evidence. */
export const OVERVIEW_EVIDENCE_THUMB_TIMEOUT_MS = 12_000;

export function buildEvidenceThumbEmptyLabel(row: {
  cameraId?: string | null;
  cameraName?: string | null;
  location?: string | null;
}): string {
  const fromLocation = String(row.location || '').trim();
  if (fromLocation && fromLocation !== 'Location not recorded') return fromLocation;
  const cameraId = String(row.cameraId || '').trim();
  if (cameraId) {
    const place = resolveCustomerCameraScopeLabel(cameraId, {}, {
      cameraName: row.cameraName,
    });
    if (place && place !== '—') return place;
    return 'Camera view';
  }
  return 'No evidence snapshot';
}

export function isSnapshotMediaAvailable(incident: Incident | null | undefined): boolean {
  if (!incident?.incident_id) return false;
  // Align with Investigate / groupedEvidenceAutoPromote — READY is playable media.
  const status = String(incident.snapshot_status || '').trim().toUpperCase();
  if (status === 'AVAILABLE' || status === 'READY') return true;
  const uri = String(incident.snapshot_uri || '').trim();
  return Boolean(uri);
}

function scopeMatches(
  incident: Incident,
  prefer?: { zoneId?: string | null; cameraId?: string | null },
): boolean {
  if (!prefer) return true;
  const wantZone = String(prefer.zoneId || '').trim();
  const wantCamera = String(prefer.cameraId || '').trim();
  if (wantZone) {
    const zone = String((incident as { zone_id?: string | null }).zone_id || '').trim();
    if (zone && zone !== wantZone) return false;
  }
  if (wantCamera) {
    const camera = String((incident as { camera_id?: string | null }).camera_id || '').trim();
    if (camera && camera !== wantCamera) return false;
  }
  return true;
}

function typeMatches(
  incident: Incident,
  preferredTypes?: string[] | null,
): boolean {
  if (!preferredTypes || preferredTypes.length === 0) return true;
  const type = String(incident.incident_type || incident.title || '').trim().toLowerCase();
  if (!type) return true;
  return preferredTypes.some((candidate) => candidate === type);
}

/**
 * Prefer newest list order among AVAILABLE/READY stills that match zone/camera
 * (and optional preferred incident types). Returns null when no honest still exists.
 */
export function pickIncidentWithAvailableSnapshot(
  incidents: Incident[] | null | undefined,
  prefer?: {
    zoneId?: string | null;
    cameraId?: string | null;
    preferredIncidentTypes?: string[] | null;
  },
): Incident | null {
  const available = (incidents || []).filter(isSnapshotMediaAvailable);
  if (available.length === 0) return null;
  const scoped = available.filter((incident) => scopeMatches(incident, prefer));
  if (scoped.length === 0) return null;
  const typed = scoped.find((incident) => typeMatches(incident, prefer?.preferredIncidentTypes));
  return typed || scoped[0] || null;
}

/** Ranked candidate incidents for blob fetch retries (typed preference first). */
export function listIncidentsWithAvailableSnapshot(
  incidents: Incident[] | null | undefined,
  prefer?: {
    zoneId?: string | null;
    cameraId?: string | null;
    preferredIncidentTypes?: string[] | null;
  },
): Incident[] {
  const available = (incidents || []).filter(isSnapshotMediaAvailable)
    .filter((incident) => scopeMatches(incident, prefer));
  if (available.length === 0) return [];
  const preferred = prefer?.preferredIncidentTypes || [];
  if (preferred.length === 0) return available;
  const typed: Incident[] = [];
  const rest: Incident[] = [];
  for (const incident of available) {
    if (typeMatches(incident, preferred)) typed.push(incident);
    else rest.push(incident);
  }
  return [...typed, ...rest];
}

export function overviewEvidenceThumbRequestKey(
  request: OverviewEvidenceThumbRequest,
): string {
  // Omit measurement windows from the cache key — thumbs deliberately ignore
  // from_date/to_date (hang risk). Window advances must not remount/revoke stills.
  return [
    String(request.conditionId || '').trim(),
    String(request.zoneId || '').trim(),
    String(request.cameraId || '').trim(),
    String(request.incidentType || '').trim(),
    (request.candidateIncidentTypes || []).join(','),
  ].join('|');
}

/**
 * Date windows for Overview thumb listIncidents probes, in order.
 * Undated scope-only only — dated multi-day lists are unsafe on yyz.
 */
export function overviewEvidenceThumbListDateProbes(
  _request: OverviewEvidenceThumbRequest,
): Array<{ fromDate?: string; toDate?: string }> {
  return [{}];
}

export function collectOverviewEvidenceThumbRequests(params: {
  nowRows: Array<{
    conditionId: string;
    zoneId?: string | null;
    cameraId?: string | null;
    incidentType?: string | null;
    conditionFamily?: string | null;
    candidateIncidentTypes?: string[] | null;
    operationalProvenance?: {
      windowStart?: string | null;
      windowEnd?: string | null;
    } | null;
  }>;
  recurringRows?: Array<{
    conditionId: string;
    zoneId?: string | null;
    cameraId?: string | null;
    incidentType?: string | null;
    conditionFamily?: string | null;
    candidateIncidentTypes?: string[] | null;
    operationalProvenance?: {
      windowStart?: string | null;
      windowEnd?: string | null;
    } | null;
  }>;
}): OverviewEvidenceThumbRequest[] {
  const byCondition = new Map<string, OverviewEvidenceThumbRequest>();
  for (const row of [...params.nowRows, ...(params.recurringRows || [])]) {
    const conditionId = String(row.conditionId || '').trim();
    if (!conditionId || byCondition.has(conditionId)) continue;
    const zoneId = String(row.zoneId || '').trim() || null;
    const cameraId = String(row.cameraId || '').trim() || null;
    if (!zoneId && !cameraId) continue;
    const incidentType = String(row.incidentType || '').trim() || null;
    const candidates = Array.from(new Set(
      [
        ...(row.candidateIncidentTypes || []),
        ...(incidentType ? [incidentType] : []),
      ]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean),
    ));
    byCondition.set(conditionId, {
      conditionId,
      zoneId,
      cameraId,
      incidentType,
      candidateIncidentTypes: candidates,
      windowStart: String(row.operationalProvenance?.windowStart || '').trim() || null,
      windowEnd: String(row.operationalProvenance?.windowEnd || '').trim() || null,
    });
  }
  return Array.from(byCondition.values());
}

export function initialEvidenceThumbState(
  request: OverviewEvidenceThumbRequest,
): OverviewEvidenceThumbState {
  return {
    status: 'idle',
    imageUrl: null,
    emptyLabel: buildEvidenceThumbEmptyLabel(request),
    incidentId: null,
  };
}
